/**
 * 邮箱登录面板：在新设备/新浏览器上，用邮箱+密码把账号带回来。
 *
 * 和 `EmailLinkRedeemSheet` 是同一个风险模型（登录会**替换当前身份**），
 * 所以复用同一套「当前账号还押着东西就先确认一次」的流程，只是把「码」换成「邮箱+密码」。
 */
import { useCallback, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import type { PublicAccount } from '@poker/shared'

import { IconAlert } from '../lobby/lobbyIcons.js'
import { groupRoomCode } from '../lobby/roomCode.js'
import { EmailAuthError, loginWithEmail } from './emailAuthApi.js'
import { IconKey } from '../icons/index.js'
import { SheetShell } from './sheetShell.js'
import type { AtRiskTable } from './atRisk.js'
import './account.css'

export type EmailLoginStage = 'input' | 'confirm' | 'working' | 'done'

function totalChips(tables: readonly AtRiskTable[]): number {
  return tables.reduce((n, t) => n + t.stack, 0)
}

/** 按下「登录」之后该发生什么。抽成纯函数，好让「有筹码时必须先确认」能被 SSR 静态渲染钉死。 */
export type EmailLoginStep =
  | { readonly kind: 'reject'; readonly message: string }
  | { readonly kind: 'confirm' }
  | { readonly kind: 'go' }

export function nextEmailLoginStep(
  email: string,
  password: string,
  atRisk: readonly AtRiskTable[] = [],
): EmailLoginStep {
  if (email.trim() === '') return { kind: 'reject', message: '请输入邮箱' }
  if (password === '') return { kind: 'reject', message: '请输入密码' }
  if (atRisk.length > 0) return { kind: 'confirm' }
  return { kind: 'go' }
}

export interface EmailLoginSheetProps {
  onClose?(): void
  /** 登录成功。调用方负责重建 WS 并刷新界面上的身份 */
  onLoggedIn?(account: PublicAccount): void
  /** 当前账号还坐着的牌桌。非空 = 登录前要确认一次 */
  readonly atRisk?: readonly AtRiskTable[]
  /** 点「忘了密码」。把当前填的邮箱带过去，省一次输入 */
  onForgotPassword?(email: string): void
  /** 注入点：测试用；默认打真接口 */
  readonly login?: (email: string, password: string) => Promise<PublicAccount>
  readonly autoFocus?: boolean
  /** 初始阶段：测试用 SSR 静态渲染每一屏，默认从头开始 */
  readonly initialStage?: EmailLoginStage
  readonly initialEmail?: string
}

export function EmailLoginSheet({
  onClose,
  onLoggedIn,
  onForgotPassword,
  atRisk = [],
  login,
  autoFocus = false,
  initialStage = 'input',
  initialEmail = '',
}: EmailLoginSheetProps): ReactNode {
  const [email, setEmail] = useState(initialEmail)
  const [password, setPassword] = useState('')
  const [stage, setStage] = useState<EmailLoginStage>(initialStage)
  const [error, setError] = useState<string | null>(null)
  const emailRef = useRef<HTMLInputElement | null>(null)

  const run = useCallback((): void => {
    setStage('working')
    setError(null)
    const call = login ?? ((e: string, p: string) => loginWithEmail(e, p))
    void call(email, password).then(
      (account) => {
        setStage('done')
        setPassword('') // 密码不在内存里多留一秒
        onLoggedIn?.(account)
      },
      (e: unknown) => {
        // message 已经是 emailAuthApi 翻好的人话，不是服务端原文
        setError(e instanceof EmailAuthError || e instanceof Error ? e.message : '没能登录，再试一次')
        setStage('input')
      },
    )
  }, [login, email, password, onLoggedIn])

  const submit = useCallback((): void => {
    const step = nextEmailLoginStep(email, password, atRisk)
    if (step.kind === 'reject') {
      setError(step.message)
      return
    }
    if (step.kind === 'confirm') {
      setStage('confirm')
      return
    }
    run()
  }, [email, password, atRisk, run])

  return (
    <SheetShell
      title="邮箱登录"
      icon={<IconKey size={20} />}
      dismissible={stage !== 'working'}
      {...(onClose === undefined ? {} : { onClose })}
    >
      {error !== null && (
        <div className="lobby-error" role="alert">
          <IconAlert size={18} />
          <span>{error}</span>
        </div>
      )}

      {stage === 'confirm' && (
        <>
          {/* 不可逆：当前这个账号没绑这个邮箱，登录过去就换身份了 */}
          <p className="acct-say">
            登录之后这台设备就换成另一个账号了。现在这个账号
            {atRisk.length === 1 ? (
              <>
                在 <span className="code">{groupRoomCode(atRisk[0]?.roomCode ?? '')}</span>{' '}
              </>
            ) : (
              <>
                在 <span className="num">{atRisk.length}</span> 张桌上{' '}
              </>
            )}
            还有 <span className="num">{totalChips(atRisk).toLocaleString()}</span> 筹码，换过去就回不来了。
          </p>
          <div className="acct-actions">
            <button type="button" className="lobby-cta pressable" onClick={run}>
              继续登录
            </button>
            <button type="button" className="lobby-btn lobby-btn--ghost" onClick={() => setStage('input')}>
              取消
            </button>
          </div>
        </>
      )}

      {stage === 'done' && (
        <>
          <p className="acct-status acct-status--ok" role="status">
            已登录，你的牌桌都在
          </p>
          <button type="button" className="lobby-cta pressable" onClick={() => onClose?.()}>
            回大厅
          </button>
        </>
      )}

      {(stage === 'input' || stage === 'working') && (
        <>
          <div className="acct-field">
            <label className="acct-field__label" htmlFor="email-login-email">
              邮箱
            </label>
            <input
              ref={emailRef}
              id="email-login-email"
              className="acct-input acct-input--text"
              type="email"
              name="email"
              value={email}
              disabled={stage === 'working'}
              autoFocus={autoFocus}
              autoComplete="username"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              placeholder="you@example.com"
              onChange={(e) => {
                setEmail(e.target.value)
                if (error !== null) setError(null)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submit()
              }}
            />
          </div>
          <div className="acct-field">
            <label className="acct-field__label" htmlFor="email-login-password">
              密码
            </label>
            <input
              id="email-login-password"
              className="acct-input acct-input--text"
              type="password"
              name="password"
              value={password}
              disabled={stage === 'working'}
              autoComplete="current-password"
              placeholder="密码"
              onChange={(e) => {
                setPassword(e.target.value)
                if (error !== null) setError(null)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submit()
              }}
            />
          </div>
          <p className="acct-status" aria-live="polite">
            {stage === 'working' ? '正在登录…' : ''}
          </p>
          <button
            type="button"
            className="lobby-cta pressable"
            disabled={stage === 'working'}
            onClick={submit}
          >
            登录
          </button>
          {/* 按钮自解释，不加说明文字（COPY 第 1 条） */}
          {onForgotPassword !== undefined && (
            <button
              type="button"
              className="lobby-btn lobby-btn--ghost"
              disabled={stage === 'working'}
              onClick={() => onForgotPassword(email)}
            >
              忘了密码
            </button>
          )}
        </>
      )}
    </SheetShell>
  )
}
